import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import type { InventoryCreatePayload, InventoryNewLookups } from '../types/inventoryNew.types';

import { categoriesService } from '@/modules/inventory/categories/services/categories.service';

const defaultLookups: InventoryNewLookups = {
  categoryOptions: [],
  unitOptions: [],
  supplierOptions: [],
  warehouseOptions: [],
};

function mapOptionRows(rows: Array<Record<string, unknown>>, nameKey = 'name') {
  return rows
    .map((row) => ({
      value: String(row.id ?? row[nameKey] ?? ''),
      label: String(row[nameKey] ?? row.name ?? ''),
    }))
    .filter((opt) => opt.label && opt.label !== 'undefined');
}

async function loadSuppliersFromPartners(companyId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('partners')
    .select('name,partner_type,is_active')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .in('partner_type', ['supplier', 'both'])
    .order('name', { ascending: true });

  if (error) return [];

  const names = (data || []).map((row) => String(row.name));
  return Array.from(new Set(names)).sort();
}

interface CategoryNode {
  id: string;
  code: string;
  name: string;
  parent_id: string | null;
  sort_order?: number;
  children: CategoryNode[];
}

function buildCategoryTree(nodes: any[]): CategoryNode[] {
  const map = new Map<string, CategoryNode>();
  const roots: CategoryNode[] = [];

  nodes.forEach(node => {
    map.set(node.id, { ...node, children: [] });
  });

  nodes.forEach(node => {
    const mappedNode = map.get(node.id)!;
    if (node.parent_id && map.has(node.parent_id)) {
      map.get(node.parent_id)!.children.push(mappedNode);
    } else {
      roots.push(mappedNode);
    }
  });

  const sortFn = (a: CategoryNode, b: CategoryNode) => {
    if (a.sort_order !== b.sort_order) {
      return (a.sort_order || 0) - (b.sort_order || 0);
    }
    return (a.name || '').localeCompare(b.name || '');
  };

  map.forEach(node => {
    node.children.sort(sortFn);
  });

  return roots.sort(sortFn);
}

function flattenTree(nodes: CategoryNode[], level = 0, sequencePrefix = ''): { value: string; label: string }[] {
  const result: { value: string; label: string }[] = [];
  
  nodes.forEach((node, index) => {
    const seq = sequencePrefix ? `${sequencePrefix}.${index + 1}` : `${index + 1}`;
    const prefix = level > 0 ? '--'.repeat(level) + ' ' : '';
    result.push({
      value: node.name,
      label: `${prefix}${seq}. ${node.name}`,
    });
    if (node.children.length > 0) {
      result.push(...flattenTree(node.children, level + 1, seq));
    }
  });

  return result;
}

function uniqueOptions(options: { value: string; label: string }[]) {
  const seen = new Set<string>();

  return options.filter((option) => {
    const key = `${option.value}::${option.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export const inventoryNewService = {
  async getLookups(companyId: string): Promise<InventoryNewLookups> {
    if (!companyId) return defaultLookups;

    const [warehousesRes, categoriesRes, inventoryGroups, suppliersFromPartners] = await Promise.all([
      supabase.from('warehouses').select('id,name').eq('company_id', companyId).order('name'),
      supabase.from('categories').select('id,code,name,parent_id,group:category_groups(code)').or(`company_id.eq.${companyId},company_id.is.null`).order('name'),
      categoriesService.fetchCategories(companyId),
      loadSuppliersFromPartners(companyId),
    ]);

    const warehouseOptions = warehousesRes.error ? [] : mapOptionRows((warehousesRes.data ?? []) as Array<Record<string, unknown>>);

    // 1. Process categories for Materials using inventory_item_groups
    const materialTree = buildCategoryTree(inventoryGroups || []);
    const categoryOptions = uniqueOptions(flattenTree(materialTree));

    const allCategories = categoriesRes.data || [];

    // 2. Process Units (Children of OTHER-UNITS or Don-Vi-Tinh)
    const unitOptions: { value: string; label: string }[] = [];
    
    // Find all potential unit parent categories (by code or name)
    const unitParents = allCategories.filter(c => {
      const code = (c.code || '').toUpperCase();
      const name = (c.name || '').toLowerCase();
      return code === 'OTHER-UNITS' || 
             code === 'DON-VI-TINH' || 
             name === ' n v tnh' ||
             name === 'don vi tinh';
    });

    if (unitParents.length > 0) {
      const parentIds = unitParents.map(p => p.id);
      const units = allCategories.filter(c => c.parent_id && parentIds.includes(c.parent_id));
      units.forEach(u => unitOptions.push({ value: u.name, label: u.name }));
    }

    // Fallback ONLY if no units found in DB hierarchy
    if (unitOptions.length === 0) {
      unitOptions.push(
        { value: 'Cy', label: 'Cy' },
        { value: 'Bao', label: 'Bao' },
        { value: 'Chai', label: 'Chai' },
        { value: 'Cun', label: 'Cun' },
        { value: 'Ci', label: 'Ci' },
        { value: 'm2', label: 'm2' },
        { value: 'm3', label: 'm3' }
      );
    }

    const supplierOptions = suppliersFromPartners.map((name) => ({ value: name, label: name }));

    return {
      warehouseOptions,
      categoryOptions,
      unitOptions,
      supplierOptions,
    };
  },

  async uploadImage(file: File, companyId: string): Promise<string | null> {
    try {
      const { storageService } = await import('@/services/storage.service');
      const result = await storageService.upload(file, {
        bucket: 'media-assets',
        folder: `${companyId}/inventory`,
      });

      if (result.error) throw new Error(result.error);
      return result.url;
    } catch (error) {
      console.error('[inventoryNewService] uploadImage error:', error);
      throw error;
    }
  },

  async createInventoryItem(payload: InventoryCreatePayload, userId: string, companyId: string): Promise<string> {
    await permissionEngine.assert(userId, 'inventory.items.create', { companyId });

    let finalCode = payload.code?.trim();
    if (!finalCode) {
      // Sinh mã tự động theo format INV-XXXXX
      const { data: latestItem } = await supabase
        .from('inventory_items')
        .select('code')
        .eq('company_id', companyId)
        .ilike('code', 'INV-%')
        .order('code', { ascending: false })
        .limit(1)
        .maybeSingle();

      let nextNumber = 1;
      if (latestItem?.code) {
        const match = latestItem.code.match(/INV-(\d+)/);
        if (match && match[1]) {
          nextNumber = parseInt(match[1], 10) + 1;
        }
      }
      finalCode = `INV-${String(nextNumber).padStart(5, '0')}`;
    }

    const itemPayload = {
      company_id: companyId,
      code: finalCode,
      name: payload.name.trim(),
      category_name: payload.category_name,
      unit: payload.unit,
      specification: payload.specification?.trim() || null,
      item_type: payload.item_type,
      minimum_stock: Number(payload.minimum_stock || 0),
      unit_price: Number(payload.unit_price || 0),
      opening_stock: Number(payload.opening_stock || 0),
      warehouse_id: payload.warehouse_id || null,
      storage_location: payload.storage_location?.trim() || null,
      origin_name: payload.origin_name?.trim() || null,
      manufacturer_code: payload.manufacturer_code?.trim() || null,
      supplier_name: payload.supplier_name || null,
      thumbnail_url: payload.thumbnail_url || null,
      is_stock_tracked: payload.is_stock_tracked,
      is_active: true,
      note: payload.note?.trim() || null,
      created_by: userId,
    };

    const { data, error } = await supabase.from('inventory_items').insert(itemPayload).select('id').single();

    if (error) throw error;

    return String(data.id);
  },
};
